import React from 'react';
import { CheckCircle, XCircle, Loader2, Clock, Sparkles } from 'lucide-react';

const MultipleKitProgress = ({ 
  isVisible, 
  progress, 
  currentStep, 
  totalSteps,
  currentProductType,
  status = 'generating',
  error = null
}) => {
  if (!isVisible) return null;

  const getStatusIcon = (stepStatus) => {
    switch (stepStatus) {
      case 'completed':
        return <CheckCircle size={20} className="text-green-500" />;
      case 'error':
        return <XCircle size={20} className="text-red-500" />;
      case 'generating':
        return <Loader2 size={20} className="text-blue-500 animate-spin" />;
      case 'pending':
        return <Clock size={20} className="text-gray-400" />;
      default:
        return <Clock size={20} className="text-gray-400" />;
    }
  };

  const getStatusColor = (stepStatus) => {
    switch (stepStatus) {
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'error':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'generating':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'pending':
        return 'text-gray-500 bg-gray-50 border-gray-200';
      default:
        return 'text-gray-500 bg-gray-50 border-gray-200';
    }
  };

  const getProgressPercentage = () => {
    if (totalSteps === 0) return 0;
    // Ensure progress doesn't exceed 100%
    const percentage = Math.round((currentStep / totalSteps) * 100);
    return Math.min(percentage, 100);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 animate-in slide-in-from-bottom-4 duration-300">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-gradient-to-br from-purple-500 to-pink-500 p-3 rounded-full">
              <Sparkles size={24} className="text-white" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Generating Multiple Kits
          </h3>
          <p className="text-gray-600 text-sm">
            Creating kits for {totalSteps} product types
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Progress</span>
            <span>{getProgressPercentage()}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${getProgressPercentage()}%`, maxWidth: '100%' }}
            ></div>
          </div>
        </div>

        {/* Current Status */}
        <div className="mb-6">
          <div className={`flex items-center justify-between p-4 rounded-lg border ${getStatusColor(status)}`}>
            <div className="flex items-center gap-3">
              {getStatusIcon(status)}
              <div>
                <p className="font-medium">
                  {status === 'generating' ? 'Generating Kit' : 
                   status === 'completed' ? 'Kit Generated' :
                   status === 'error' ? 'Generation Failed' : 'Pending'}
                </p>
                <p className="text-sm opacity-75">
                  {currentProductType || 'Unknown Product Type'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-medium">{currentStep} of {totalSteps}</p>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <XCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-600 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Progress Details */}
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Successfully Generated:</span>
            <span className="font-medium text-green-600">
              {currentStep}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Currently Processing:</span>
            <span className="font-medium text-blue-600">
              {status === 'generating' ? 1 : 0}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Remaining:</span>
            <span className="font-medium text-orange-600">
              {Math.max(0, totalSteps - currentStep - (status === 'generating' ? 1 : 0))}
            </span>
          </div>
          {error && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Failed:</span>
              <span className="font-medium text-red-600">1</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex gap-3">
          {status === 'error' && (
            <button 
              className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg font-medium transition-colors duration-200"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          )}
          {status === 'completed' && currentStep === totalSteps && (
            <button 
              className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg font-medium transition-colors duration-200"
              onClick={() => window.location.href = '/my-products'}
            >
              View Kits
            </button>
          )}
        </div>

        {/* Tips */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-700">
            💡 Each kit is generated separately to ensure the best quality. This may take a few minutes for multiple kits.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MultipleKitProgress;
